import { useEffect, useState } from "react";
import { Camera, Sparkles, ShoppingBasket } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExplainerVideo } from "./ExplainerVideo";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Triggered by "Try it now" / "Get started" — caller scrolls to capture. */
  onTryItNow: () => void;
};

const PANELS = [
  {
    icon: Camera,
    title: "Capture anything",
    body: "Upload a photo, paste a link or add text.",
  },
  {
    icon: Sparkles,
    title: "We organise it",
    body: "Ingredients, steps & quantities structured instantly.",
  },
  {
    icon: ShoppingBasket,
    title: "Plan & shop",
    body: "Build menus & generate smart shopping lists.",
  },
];

const prefersReducedMotion = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
};

/**
 * "How it works" surface. Default view is the 20-second animated explainer
 * (CleanPlate's magic moment + premium features). Users can skip to the
 * static 3-panel summary, and visitors with reduced-motion preferences
 * land on the panels by default.
 */
export const HowItWorksDialog = ({ open, onOpenChange, onTryItNow }: Props) => {
  const [showPanels, setShowPanels] = useState(false);

  // Default to panels when the user has reduced-motion enabled. Reset whenever
  // the dialog re-opens so returning visitors see the video again.
  useEffect(() => {
    if (open) setShowPanels(prefersReducedMotion());
  }, [open]);

  const start = () => {
    onOpenChange(false);
    onTryItNow();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">How it works</DialogTitle>
          <DialogDescription>
            {showPanels
              ? "Three steps from chaos to a clean kitchen."
              : "A 20-second tour of the magic — & what Premium unlocks."}
          </DialogDescription>
        </DialogHeader>

        {showPanels ? (
          <>
            <div className="mt-2 space-y-3">
              {PANELS.map(({ icon: Icon, title, body }, i) => (
                <div
                  key={title}
                  className="flex items-start gap-4 rounded-xl border border-border bg-card/60 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="flex-1">
                    <p className="font-display text-base font-semibold text-foreground">
                      <span className="mr-2 text-primary">{i + 1}.</span>
                      {title}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">{body}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button className="mt-2 h-12 w-full" onClick={start}>
              Try it now
            </Button>
          </>
        ) : (
          <ExplainerVideo
            playing={open}
            onCta={start}
            onSkip={() => setShowPanels(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
